/** Enterprise customer API — GET /enterprise/me and payment proof. */
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import useAuthStore from "@/stores/authStore";

export interface EnterprisePackage {
  company: { _id: string; name: string; country: string; currency: string };
  price: number | null;
  billingCycle: "monthly" | "quarterly" | "yearly";
  cycleLabel: string;
  perMonth: number | null;
  currency: string;
  paymentTerms: "invoice" | "pay_online";
  invoiceDueDays: number;
  setupFee: number;
  contract: { start: string | null; months: number | null };
  limits: { users: number | null; branches: number | null; businesses: number | null };
  features: { key: string; label: string; on: boolean }[];
  inclusions: string[];
  accountManager: { name: string; email: string; phone: string | null } | null;
  businesses: { _id: string; name: string; industry: string; branches: string[] }[];
}

export interface EnterprisePaymentRow {
  _id: string;
  kind: "first" | "renewal";
  amountMinor: number;
  currency: string;
  method: "bank_transfer" | "mobile_money" | "cheque" | "cash" | "card";
  reference: string;
  paidAt: string;
  payerName: string | null;
  note: string | null;
  hasReceipt: boolean;
  receiptName: string | null;
  status: "submitted" | "confirmed" | "rejected" | "withdrawn";
  reviewNote: string | null;
  confirmedAmountMinor: number | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface PaymentInstructions {
  bankName: string | null;
  accountName: string | null;
  accountNumber: string | null;
  branch: string | null;
  swift: string | null;
  momoNetwork: string | null;
  momoNumber: string | null;
  momoName: string | null;
  note: string | null;
}

export type EnterpriseMe =
  | { enterprise: false }
  | {
      enterprise: true;
      status: string;
      awaitingPayment: boolean;
      paidUntil: string | null;
      activatedAt: string | null;
      welcomeSeenAt: string | null;
      canPay: boolean;
      dueMinor: number;
      package: EnterprisePackage;
      instructions: PaymentInstructions | null;
      payments: EnterprisePaymentRow[];
      hasOpenSubmission: boolean;
    };

export const enterpriseKey = ["enterprise", "me"] as const;

export const enterpriseService = {
  me: async () => (await api.get<{ data: EnterpriseMe }>("/enterprise/me")).data.data,
  welcomeSeen: async () => api.post("/enterprise/me/welcome-seen"),
  submitPayment: async (form: FormData) => (await api.post<{ data: EnterprisePaymentRow }>("/enterprise/me/payments", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90_000 })).data.data,
  withdraw: async (id: string) => (await api.post(`/enterprise/me/payments/${id}/withdraw`)).data.data,
  receipt: async (id: string) => (await api.get<{ data: { url: string } }>(`/enterprise/me/payments/${id}/receipt`)).data.data.url,
};

/** The signed-in company's enterprise status (no-op for other plans). */
export function useEnterprise() {
  const token = useAuthStore((s) => s.token);
  return useQuery({
    queryKey: enterpriseKey,
    queryFn: enterpriseService.me,
    enabled: !!token,
    staleTime: 60_000,
    retry: false,
  });
}
